import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Handshake, Plus, Trash2, Pencil, Save, X, Send, Sparkles,
  Image as ImgIcon, Video as VideoIcon, MessageCircle, ExternalLink,
  Building2, Globe, Mail, Phone, MapPin, Wand2, Loader2, Bell, Clock,
  BarChart3, TrendingUp, TrendingDown, Minus, Upload, FileSpreadsheet, CheckCircle2, AlertCircle,
} from "lucide-react";

const emptyForm = {
  company_name: "", country: "", website: "", email: "", whatsapp: "",
  phone: "", address: "",
  products: [], destinations: "", notes: "",
};

const STATUS_STYLES = {
  in_progress: "bg-amber-100 text-amber-800",
  contacted: "bg-sky-100 text-sky-800",
  active: "bg-emerald-100 text-emerald-800",
  dormant: "bg-slate-100 text-slate-700",
};

const FOLLOWUP_LABEL = { 1: "3-day", 2: "7-day", 3: "14-day+" };
const FOLLOWUP_STYLE = {
  1: "bg-amber-50 text-amber-800 border-amber-200",
  2: "bg-orange-50 text-orange-800 border-orange-200",
  3: "bg-red-50 text-red-700 border-red-200",
};

function FollowupBadge({ stage, days }) {
  if (!stage) return null;
  return (
    <span title={`Contacted ${days} day${days === 1 ? "" : "s"} ago — ${FOLLOWUP_LABEL[stage]} follow-up due`}
      className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${FOLLOWUP_STYLE[stage]} inline-flex items-center gap-1`}>
      <Bell size={10} /> {FOLLOWUP_LABEL[stage]}
    </span>
  );
}

// ==========================================================
// Pipeline Report modal
// ==========================================================
function DeltaChip({ v, goodPositive = true }) {
  if (v === 0) return <span className="text-xs text-[color:var(--aii-muted)] inline-flex items-center gap-1"><Minus size={10}/> 0</span>;
  const up = v > 0;
  const positive = goodPositive ? up : !up;
  const cls = positive ? "text-emerald-700" : "text-red-600";
  const Icon = up ? TrendingUp : TrendingDown;
  return <span className={`text-xs font-semibold inline-flex items-center gap-1 ${cls}`}><Icon size={11}/> {Math.abs(v)}</span>;
}

function PipelineModal({ onClose }) {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/pipeline/report").then(({ data }) => setData(data)).catch(() => toast.error("Could not load pipeline report"));
  }, []);

  const emailIt = async () => {
    const to = window.prompt("Email pipeline report to:", "");
    if (!to) return;
    setSending(true);
    try {
      await api.post("/pipeline/report/email", { recipient_email: to, week_of: data?.week_of });
      toast.success(`Report emailed to ${to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="pipeline-modal">
        <div className="p-5 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-3 flex-wrap sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Business Development</div>
            <h2 className="font-display font-semibold text-xl flex items-center gap-2"><BarChart3 className="text-brand" size={20}/> BDOM Pipeline Report</h2>
            {data && <div className="text-xs text-[color:var(--aii-muted)] mt-1">Week of {data.week_of} · vs previous week</div>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={emailIt} disabled={!data || sending} data-testid="pipeline-email"
              className="text-sm px-3 py-1.5 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1.5 disabled:opacity-50">
              <Mail size={13}/> {sending ? "Sending…" : "Email"}
            </button>
            <button onClick={onClose} className="p-1 text-[color:var(--aii-muted)] hover:text-[color:var(--aii-text)]"><X size={16}/></button>
          </div>
        </div>

        {!data ? (
          <div className="p-10 text-center text-[color:var(--aii-muted)]"><Loader2 className="mx-auto animate-spin"/> Computing…</div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Metric grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: "New partners", key: "new_partners" },
                { label: "Emails sent", key: "emails_sent" },
                { label: "Meetings requested", key: "meetings_requested" },
                { label: "Meetings confirmed", key: "meetings_confirmed" },
                { label: "Conversion (%)", key: "conversion_rate" },
                { label: "Follow-ups pending", key: "followups_pending", goodPositive: false },
              ].map((m) => (
                <div key={m.key} className="aii-card p-4">
                  <div className="text-xs text-[color:var(--aii-muted)]">{m.label}</div>
                  <div className="text-3xl font-display font-semibold mt-1">{data.this_week[m.key]}</div>
                  <div className="mt-1"><DeltaChip v={data.delta[m.key]} goodPositive={m.goodPositive !== false}/> vs last week</div>
                </div>
              ))}
            </div>

            {/* New partners this week */}
            <div>
              <h3 className="font-semibold text-sm mb-2">New partners this week</h3>
              {data.this_week.new_partner_names?.length ? (
                <div className="flex flex-wrap gap-2">
                  {data.this_week.new_partner_names.map((n) => (
                    <span key={n} className="text-xs px-2 py-1 bg-[color:var(--aii-green-tint)] text-brand-dark rounded">{n}</span>
                  ))}
                </div>
              ) : <div className="text-sm text-[color:var(--aii-muted)]">None added this week.</div>}
            </div>

            {/* By country */}
            <div>
              <h3 className="font-semibold text-sm mb-2">Emails by country</h3>
              {Object.keys(data.this_week.by_country || {}).length ? (
                <div className="aii-card overflow-x-auto">
                  <table className="aii-table">
                    <thead><tr><th>Country</th><th className="text-right">Emails</th></tr></thead>
                    <tbody>
                      {Object.entries(data.this_week.by_country).sort((a, b) => b[1] - a[1]).map(([c, n]) => (
                        <tr key={c}><td>{c === "—" ? <span className="text-[color:var(--aii-muted)]">Unspecified</span> : c}</td><td className="text-right font-semibold">{n}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <div className="text-sm text-[color:var(--aii-muted)]">No emails sent this week.</div>}
            </div>

            <div className="text-xs text-[color:var(--aii-muted)] pt-2 border-t border-[color:var(--aii-border)]">
              This report is emailed to the Founder every Monday morning automatically.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusPill({ s }) {
  const cls = STATUS_STYLES[s || "in_progress"] || STATUS_STYLES.in_progress;
  return <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${cls}`}>{(s || "in_progress").replace("_", " ")}</span>;
}

// ==========================================================
// Partner revenue cell — this-month + this-FY totals per currency
// ==========================================================
const CUR_SYM = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };

function PartnerRevenueCell({ rev }) {
  if (!rev) return <span className="text-[color:var(--aii-muted)] text-xs">—</span>;
  const fmt = (bag) => {
    const parts = Object.entries(bag || {})
      .filter(([, v]) => v > 0)
      .map(([c, v]) => `${CUR_SYM[c] || c}${Math.round(v).toLocaleString()}`);
    return parts.length ? parts.join(" · ") : "—";
  };
  const monthLabel = new Date().toLocaleString(undefined, { month: "short" });
  return (
    <div className="text-xs leading-tight">
      <div className="font-semibold text-brand">{fmt(rev.this_fy)}</div>
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)]">FY {rev.fy || ""}</div>
      <div className="text-[color:var(--aii-text-2)] mt-1">{fmt(rev.this_month)}</div>
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)]">{monthLabel}</div>
    </div>
  );
}

// ==========================================================
// Add / Edit partner drawer
// ==========================================================
function PartnerForm({ initial, products, onCancel, onSave }) {
  const [f, setF] = useState(initial || emptyForm);
  const [busy, setBusy] = useState(false);
  const toggleProd = (p) => setF((x) => ({ ...x, products: x.products.includes(p) ? x.products.filter((z) => z !== p) : [...x.products, p] }));

  const submit = async () => {
    if (!f.company_name.trim()) { toast.error("Company name is required"); return; }
    setBusy(true);
    await onSave(f);
    setBusy(false);
  };

  return (
    <div className="aii-card p-5 space-y-4" data-testid="partner-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold text-lg flex items-center gap-2">
          {initial?.id ? <Pencil size={16} /> : <Plus size={16} />}
          {initial?.id ? "Edit partner" : "Add partner"}
        </h3>
        <button onClick={onCancel} className="text-[color:var(--aii-muted)] hover:text-[color:var(--aii-text)]"><X size={16} /></button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <div className="text-xs text-[color:var(--aii-muted)] mb-1">Company name *</div>
          <input data-testid="p-company" className="input-aii w-full" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} />
        </label>
        <label className="block">
          <div className="text-xs text-[color:var(--aii-muted)] mb-1">Country</div>
          <input data-testid="p-country" className="input-aii w-full" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} />
        </label>
        <label className="block">
          <div className="text-xs text-[color:var(--aii-muted)] mb-1">Website</div>
          <input data-testid="p-website" className="input-aii w-full" placeholder="https://…" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} />
        </label>
        <label className="block">
          <div className="text-xs text-[color:var(--aii-muted)] mb-1">Email</div>
          <input data-testid="p-email" className="input-aii w-full" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        </label>
        <label className="block">
          <div className="text-xs text-[color:var(--aii-muted)] mb-1">WhatsApp no.</div>
          <input data-testid="p-whatsapp" className="input-aii w-full" placeholder="+91…" value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} />
        </label>
        <label className="block">
          <div className="text-xs text-[color:var(--aii-muted)] mb-1">Billing phone</div>
          <input data-testid="p-phone" className="input-aii w-full" placeholder="+91…" value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} />
        </label>
        <label className="block sm:col-span-2">
          <div className="text-xs text-[color:var(--aii-muted)] mb-1">Billing address</div>
          <textarea data-testid="p-address" className="input-aii w-full" rows={2} placeholder="Street, city, postcode, country" value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} />
        </label>
        <label className="block sm:col-span-2">
          <div className="text-xs text-[color:var(--aii-muted)] mb-1">Destinations they offer</div>
          <input data-testid="p-destinations" className="input-aii w-full" placeholder="e.g., UK, Ireland, Iceland" value={f.destinations} onChange={(e) => setF({ ...f, destinations: e.target.value })} />
        </label>
      </div>

      <div>
        <div className="text-xs text-[color:var(--aii-muted)] mb-2">Products (tick all that apply)</div>
        <div className="flex flex-wrap gap-2">
          {products.map((p) => (
            <label key={p} className={`text-sm px-3 py-1.5 rounded-full border cursor-pointer select-none flex items-center gap-1.5 ${f.products.includes(p) ? "bg-brand text-white border-brand" : "border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]"}`}>
              <input type="checkbox" className="hidden" checked={f.products.includes(p)} onChange={() => toggleProd(p)} data-testid={`p-prod-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
              {p}
            </label>
          ))}
        </div>
      </div>

      <label className="block">
        <div className="text-xs text-[color:var(--aii-muted)] mb-1">Notes</div>
        <textarea className="input-aii w-full" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </label>

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="text-sm px-3 py-1.5 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)]">Cancel</button>
        <button data-testid="p-save" onClick={submit} disabled={busy} className="text-sm px-3 py-1.5 bg-brand text-white rounded hover:bg-brand-dark flex items-center gap-1.5 disabled:opacity-50">
          <Save size={13} /> {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

// ==========================================================
// Outreach Composer (top of the page)
// ==========================================================
function OutreachComposer({ partners, products, onSent }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [logo, setLogo] = useState(null);       // { public_url, kind }
  const [media, setMedia] = useState(null);
  const [headline, setHeadline] = useState("");
  const [offer, setOffer] = useState("");
  const [chosenProducts, setChosenProducts] = useState([]);
  const [subject, setSubject] = useState("");
  const [selected, setSelected] = useState([]);   // partner ids
  const logoRef = useRef(null);
  const mediaRef = useRef(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPartner, setPreviewPartner] = useState(null);
  const [previewCopy, setPreviewCopy] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/outreach/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const onPickLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { setLogo(await uploadFile(file)); toast.success("Logo uploaded"); } catch { toast.error("Upload failed"); }
  };
  const onPickMedia = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { setMedia(await uploadFile(file)); toast.success(`${file.type.startsWith("video/") ? "Video" : "Picture"} uploaded`); }
    catch { toast.error("Upload failed"); }
  };

  const toggleP = (p) => setChosenProducts((x) => x.includes(p) ? x.filter((z) => z !== p) : [...x, p]);
  const toggleId = (id) => setSelected((x) => x.includes(id) ? x.filter((z) => z !== id) : [...x, id]);
  const selectAll = () => setSelected(partners.filter((p) => p.email).map((p) => p.id));
  const clearSel = () => setSelected([]);

  const preview = async (partnerId) => {
    setAiLoading(true);
    setPreviewPartner(partners.find((p) => p.id === partnerId));
    setPreviewOpen(true);
    setPreviewCopy("");
    try {
      const { data } = await api.post("/outreach/preview-copy", { partner_id: partnerId, headline, offer, products: chosenProducts });
      setPreviewCopy(data.body);
    } catch { toast.error("AI preview failed"); setPreviewOpen(false); }
    setAiLoading(false);
  };

  const send = async () => {
    if (selected.length === 0) { toast.error("Select at least one partner"); return; }
    if (!window.confirm(`Send outreach email to ${selected.length} partner${selected.length === 1 ? "" : "s"}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/outreach/send", {
        partner_ids: selected,
        subject,
        headline,
        offer,
        products: chosenProducts,
        logo_url: logo?.public_url || null,
        media_url: media?.public_url || null,
        media_kind: media?.kind || null,
        use_ai_copy: true,
      });
      toast.success(`${data.summary.sent} sent · ${data.summary.failed} failed`);
      // Copy WhatsApp click-links to clipboard for those partners who had a WA number.
      const wa = data.results.filter((r) => r.whatsapp_link).map((r) => `${r.company_name}: ${r.whatsapp_link}`).join("\n");
      if (wa) {
        try { await navigator.clipboard.writeText(wa); toast.info("WhatsApp click-links copied to clipboard"); }
        catch (err) { console.debug("clipboard write failed:", err?.message); }
      }
      onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    setBusy(false);
  };

  const eligible = partners.filter((p) => p.email).length;

  return (
    <div className="aii-card p-5" data-testid="outreach-composer">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Outreach Studio</div>
          <h2 className="font-display font-semibold text-xl flex items-center gap-2"><Sparkles className="text-brand" size={18} /> Create graphic &amp; send to partners</h2>
        </div>
        <button data-testid="composer-toggle" onClick={() => setOpen((o) => !o)}
          className={`text-sm px-3 py-2 rounded flex items-center gap-1.5 ${open ? "border border-[color:var(--aii-border)]" : "bg-brand text-white hover:bg-brand-dark"}`}>
          {open ? <X size={14} /> : <Plus size={14} />} {open ? "Close" : "Compose outreach"}
        </button>
      </div>

      {open && (
        <div className="mt-4 space-y-4">
          {/* Uploads + text */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg border border-dashed border-[color:var(--aii-border)] flex flex-col items-center justify-center text-center min-h-[140px]">
              <ImgIcon size={22} className="text-brand" />
              <div className="text-sm font-semibold mt-2">Logo</div>
              <div className="text-xs text-[color:var(--aii-muted)] mt-0.5">PNG / JPG · shown at the top</div>
              {logo && <img src={logo.public_url} alt="logo" className="max-h-16 mt-2" />}
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={onPickLogo} data-testid="upload-logo" />
              <button onClick={() => logoRef.current?.click()} className="mt-2 text-xs px-3 py-1 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)]">
                {logo ? "Change" : "Upload"}
              </button>
            </div>
            <div className="p-4 rounded-lg border border-dashed border-[color:var(--aii-border)] flex flex-col items-center justify-center text-center min-h-[140px] lg:col-span-2">
              {media?.kind === "video" ? <VideoIcon size={22} className="text-brand" /> : <ImgIcon size={22} className="text-brand" />}
              <div className="text-sm font-semibold mt-2">Hero picture or video</div>
              <div className="text-xs text-[color:var(--aii-muted)] mt-0.5">JPG / PNG / MP4 · up to 25 MB</div>
              {media?.kind === "image" && <img src={media.public_url} alt="hero" className="mt-2 max-h-32 rounded" />}
              {media?.kind === "video" && <div className="mt-2 text-xs text-brand break-all">{media.public_url}</div>}
              <input ref={mediaRef} type="file" accept="image/*,video/mp4,video/quicktime" className="hidden" onChange={onPickMedia} data-testid="upload-media" />
              <button onClick={() => mediaRef.current?.click()} className="mt-2 text-xs px-3 py-1 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)]">
                {media ? "Change" : "Upload"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs text-[color:var(--aii-muted)] mb-1">Email subject (optional)</div>
              <input data-testid="o-subject" className="input-aii w-full" placeholder="Auto-generated if empty" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </label>
            <label className="block">
              <div className="text-xs text-[color:var(--aii-muted)] mb-1">Headline (shown large in the graphic)</div>
              <input data-testid="o-headline" className="input-aii w-full" placeholder="e.g., India is opening up to educational travel again" value={headline} onChange={(e) => setHeadline(e.target.value)} />
            </label>
          </div>
          <label className="block">
            <div className="text-xs text-[color:var(--aii-muted)] mb-1">Partnership offer / terms</div>
            <textarea data-testid="o-offer" className="input-aii w-full" rows={2} placeholder="e.g., 15% commission on first 20 bookings + free FAM trip." value={offer} onChange={(e) => setOffer(e.target.value)} />
          </label>

          <div>
            <div className="text-xs text-[color:var(--aii-muted)] mb-2">Programs being offered</div>
            <div className="flex flex-wrap gap-2">
              {products.map((p) => (
                <button key={p} type="button" onClick={() => toggleP(p)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${chosenProducts.includes(p) ? "bg-brand text-white border-brand" : "border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]"}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Select partners */}
          <div className="border-t border-[color:var(--aii-border)] pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold">Select recipients ({selected.length} / {eligible} with email)</div>
              <div className="flex gap-2 text-xs">
                <button onClick={selectAll} className="underline text-brand">Select all</button>
                <button onClick={clearSel} className="underline text-[color:var(--aii-muted)]">Clear</button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1">
              {partners.map((p) => {
                const disabled = !p.email;
                return (
                  <label key={p.id} className={`flex items-start gap-2 p-2 rounded border ${selected.includes(p.id) ? "border-brand bg-[color:var(--aii-green-tint)]" : "border-[color:var(--aii-border)]"} ${disabled ? "opacity-50" : "cursor-pointer hover:bg-[color:var(--aii-green-tint)]"}`}>
                    <input type="checkbox" disabled={disabled} checked={selected.includes(p.id)} onChange={() => toggleId(p.id)} data-testid={`sel-${p.id}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{p.company_name}</div>
                      <div className="text-xs text-[color:var(--aii-muted)] truncate">{p.country || "—"} · {p.email || "No email"}</div>
                    </div>
                    {p.email && (
                      <button type="button" onClick={(e) => { e.preventDefault(); preview(p.id); }} title="Preview AI copy"
                        className="text-[color:var(--aii-muted)] hover:text-brand p-1"><Wand2 size={12} /></button>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end">
            <button data-testid="o-send" onClick={send} disabled={busy || selected.length === 0}
              className="text-sm px-4 py-2 bg-brand text-white rounded hover:bg-brand-dark flex items-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {busy ? "Sending…" : `Send outreach (${selected.length})`}
            </button>
          </div>
        </div>
      )}

      {/* AI preview modal */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPreviewOpen(false)}>
          <div className="bg-white max-w-lg w-full rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-[color:var(--aii-border)] flex items-center justify-between">
              <div>
                <div className="text-xs text-[color:var(--aii-muted)]">AI email copy preview</div>
                <div className="font-semibold">{previewPartner?.company_name}</div>
              </div>
              <button onClick={() => setPreviewOpen(false)}><X size={16} /></button>
            </div>
            <div className="p-4 whitespace-pre-wrap text-sm min-h-[200px]">
              {aiLoading ? <div className="flex items-center gap-2 text-[color:var(--aii-muted)]"><Loader2 size={14} className="animate-spin" /> Generating…</div> : previewCopy}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================================
// Excel Bulk Upload modal — parse .xlsx/.csv, tag with a source
// (Google / Lead / Data Extraction), preview counts, save in one shot.
// ==========================================================
function BulkUploadModal({ sourceOptions, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [source, setSource] = useState(sourceOptions[0] || "Google");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const submit = async () => {
    if (!file) { toast.error("Choose an Excel or CSV file first"); return; }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", source);
      const { data } = await api.post("/partners/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.inserted > 0) toast.success(`${data.inserted} partner(s) added · source: ${data.source}`);
      else toast.warning(`Nothing inserted (skipped ${data.skipped}${data.duplicates?.length ? `, ${data.duplicates.length} duplicates` : ""})`);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) setFile(f);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-lg w-full rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="bulk-upload-modal">
        <div className="p-5 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Bulk import</div>
            <h2 className="font-display font-semibold text-lg flex items-center gap-2"><FileSpreadsheet className="text-brand" size={18}/> Upload partners</h2>
            <div className="text-xs text-[color:var(--aii-muted)] mt-1">Excel (.xlsx) or CSV. Duplicate company names are skipped automatically.</div>
          </div>
          <button onClick={onClose} className="p-1 text-[color:var(--aii-muted)] hover:text-[color:var(--aii-text)]"><X size={16}/></button>
        </div>

        <div className="p-5 space-y-4">
          <label className="block">
            <div className="text-xs text-[color:var(--aii-muted)] mb-1">Where did these partners come from?</div>
            <select data-testid="bulk-source" className="input-aii w-full" value={source} onChange={(e) => setSource(e.target.value)}>
              {sourceOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition ${dragOver ? "border-brand bg-[color:var(--aii-green-tint)]" : "border-[color:var(--aii-border)]"}`}
          >
            <Upload className="mx-auto text-[color:var(--aii-muted)]" size={22}/>
            <div className="text-sm mt-2">Drop your Excel / CSV here or</div>
            <label className="inline-block mt-2">
              <span className="text-xs px-3 py-1.5 bg-brand text-white rounded cursor-pointer hover:bg-brand-dark">Choose file</span>
              <input type="file" data-testid="bulk-file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)}/>
            </label>
            {file && <div className="text-xs text-[color:var(--aii-text-2)] mt-2 truncate">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
          </div>

          <div className="text-[11px] text-[color:var(--aii-muted)] leading-relaxed">
            Column headers accepted (case-insensitive):{" "}
            <b>company_name</b> (or name / company), country, email, website, whatsapp, phone, address, destinations, products, notes.
          </div>

          {result && (
            <div className="border border-[color:var(--aii-border)] rounded p-3 text-sm space-y-1" data-testid="bulk-result">
              <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 size={14}/> {result.inserted} inserted</div>
              {result.skipped > 0 && <div className="flex items-center gap-2 text-amber-700"><AlertCircle size={14}/> {result.skipped} skipped</div>}
              {result.duplicates?.length > 0 && (
                <div className="text-xs text-[color:var(--aii-muted)]">
                  Duplicates: {result.duplicates.slice(0, 5).join(", ")}{result.duplicates.length > 5 ? ` +${result.duplicates.length - 5} more` : ""}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-[color:var(--aii-border)] flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-3 py-1.5 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)]">Close</button>
          <button data-testid="bulk-upload-submit" onClick={submit} disabled={busy || !file}
            className="text-sm px-3 py-1.5 bg-brand text-white rounded hover:bg-brand-dark flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <><Loader2 size={13} className="animate-spin"/> Uploading…</> : <><Upload size={13}/> Import</>}
          </button>
        </div>
      </div>
    </div>
  );
}


// ==========================================================
// Main page
// ==========================================================
export default function Partners() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [sources, setSources] = useState(["Google", "Lead", "Data Extraction"]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPipeline, setShowPipeline] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [revenue, setRevenue] = useState({});   // {partner_id: {total, this_month, this_fy, monthly, fy}}

  const load = async () => {
    const [{ data: partners }, { data: opts }, srcResp, revResp] = await Promise.all([
      api.get("/partners"),
      api.get("/partners/product-options"),
      api.get("/partners/source-options").catch(() => ({ data: null })),
      api.get("/partners/revenue-summary").catch(() => ({ data: {} })),
    ]);
    setRows(partners);
    setProducts(opts);
    if (srcResp.data) setSources(srcResp.data);
    setRevenue(revResp.data || {});
  };
  useEffect(() => { load(); }, []);

  const save = async (f) => {
    try {
      if (editing?.id) {
        await api.patch(`/partners/${editing.id}`, f);
        toast.success("Partner updated");
      } else {
        await api.post("/partners", f);
        toast.success("Partner added");
      }
      setShowForm(false); setEditing(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this partner?")) return;
    await api.delete(`/partners/${id}`);
    toast.success("Deleted"); await load();
  };

  const markFollowedUp = async (id) => {
    try {
      await api.post(`/partners/${id}/followup-sent`);
      toast.success("Follow-up logged · clock reset");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const followupDue = useMemo(() => rows.filter((r) => r.followup_due > 0), [rows]);

  const summary = useMemo(() => ({
    total: rows.length,
    with_email: rows.filter((r) => r.email).length,
    contacted: rows.filter((r) => r.status === "contacted" || r.status === "active").length,
  }), [rows]);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Business Development</div>
          <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2"><Handshake className="text-brand" /> Partners in Progress</h1>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Track prospective partners, compose an on-brand outreach graphic, and send it with one click.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="open-bulk-upload" onClick={() => setShowBulkUpload(true)}
            className="text-sm px-3 py-2 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1.5">
            <FileSpreadsheet size={14}/> Upload Excel
          </button>
          <button data-testid="open-pipeline" onClick={() => setShowPipeline(true)}
            className="text-sm px-3 py-2 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1.5">
            <BarChart3 size={14}/> Pipeline report
          </button>
          <div className="flex gap-2 text-xs">
            <div className="px-3 py-2 aii-card"><b className="text-lg">{summary.total}</b><div className="text-[color:var(--aii-muted)]">total</div></div>
            <div className="px-3 py-2 aii-card"><b className="text-lg">{summary.with_email}</b><div className="text-[color:var(--aii-muted)]">with email</div></div>
            <div className="px-3 py-2 aii-card"><b className="text-lg">{summary.contacted}</b><div className="text-[color:var(--aii-muted)]">contacted+</div></div>
          </div>
        </div>
      </div>

      {showPipeline && <PipelineModal onClose={() => setShowPipeline(false)} />}
      {showBulkUpload && <BulkUploadModal sourceOptions={sources} onClose={() => setShowBulkUpload(false)} onDone={load}/>}

      <OutreachComposer partners={rows} products={products} onSent={load} />

      {followupDue.length > 0 && (
        <div className="aii-card p-4 border-l-4 border-amber-500" data-testid="followup-strip">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Bell className="text-amber-600" size={18} />
              <div>
                <div className="font-display font-semibold text-base">
                  {followupDue.length} follow-up{followupDue.length === 1 ? "" : "s"} due
                </div>
                <div className="text-xs text-[color:var(--aii-text-2)]">Contacted 3+ days ago with no meeting yet — nudge them today.</div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {followupDue.map((p) => (
              <div key={p.id} data-testid={`followup-${p.id}`} className="flex items-start justify-between gap-2 p-2 rounded border border-[color:var(--aii-border)] bg-white">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate flex items-center gap-2">
                    {p.company_name}
                    <FollowupBadge stage={p.followup_due} days={p.days_since_contact} />
                  </div>
                  <div className="text-xs text-[color:var(--aii-muted)] flex items-center gap-1 mt-0.5">
                    <Clock size={10} /> {p.days_since_contact}d ago · {p.country || "—"}
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {p.whatsapp && (
                    <a href={`https://wa.me/${p.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(`Hi ${p.company_name} — just following up on our partnership email. Would love to find a time to chat. — Aii Group`)}`} target="_blank" rel="noreferrer" title="Send WhatsApp nudge" onClick={() => markFollowedUp(p.id)}
                      className="text-[10px] px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center justify-center gap-1"><MessageCircle size={10}/> WhatsApp</a>
                  )}
                  {p.email && (
                    <a href={`mailto:${p.email}?subject=${encodeURIComponent(`Following up · Aii Group partnership`)}&body=${encodeURIComponent(`Hi ${p.company_name} team,\n\nJust circling back on my note earlier — would love to explore how Aii Group can support your travellers.\n\nWould you have 20 minutes this week for an intro call?\n\nThanks,\n`)}`} title="Send email nudge" onClick={() => markFollowedUp(p.id)}
                      className="text-[10px] px-2 py-1 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] flex items-center justify-center gap-1"><Mail size={10}/> Email</a>
                  )}
                  <button onClick={() => markFollowedUp(p.id)} data-testid={`followup-done-${p.id}`} title="Mark chased (resets timer)"
                    className="text-[10px] px-2 py-1 text-[color:var(--aii-muted)] hover:text-brand">Mark done</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="font-display font-semibold text-lg">Partner directory</h2>
        <button data-testid="add-partner" onClick={() => { setEditing(null); setShowForm(true); }}
          className="text-sm px-3 py-2 bg-brand text-white rounded hover:bg-brand-dark flex items-center gap-1.5"><Plus size={14} /> Add partner</button>
      </div>

      {showForm && <PartnerForm initial={editing} products={products} onCancel={() => { setShowForm(false); setEditing(null); }} onSave={save} />}

      <div className="aii-card overflow-x-auto">
        <table className="aii-table">
          <thead>
            <tr>
              <th><Building2 size={11} className="inline mr-1" /> Company</th>
              <th><Globe size={11} className="inline mr-1" /> Country</th>
              <th><Mail size={11} className="inline mr-1" /> Email</th>
              <th><Phone size={11} className="inline mr-1" /> WhatsApp</th>
              <th>Products</th>
              <th><MapPin size={11} className="inline mr-1" /> Destinations</th>
              <th className="text-right">Revenue</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-[color:var(--aii-muted)]">No partners yet. Add your first one above.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`row-${r.id}`}>
                <td>
                  <div className="font-medium flex items-center gap-2">
                    {r.company_name}
                    <FollowupBadge stage={r.followup_due} days={r.days_since_contact} />
                  </div>
                  {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="text-xs text-brand hover:underline flex items-center gap-1"><ExternalLink size={10} />{r.website.replace(/^https?:\/\//, "")}</a>}
                </td>
                <td>{r.country || "—"}</td>
                <td>{r.email ? <a href={`mailto:${r.email}`} className="text-brand hover:underline">{r.email}</a> : <span className="text-[color:var(--aii-muted)]">—</span>}</td>
                <td>
                  {r.whatsapp ? (
                    <a href={`https://wa.me/${r.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline flex items-center gap-1"><MessageCircle size={11} />{r.whatsapp}</a>
                  ) : <span className="text-[color:var(--aii-muted)]">—</span>}
                </td>
                <td>
                  <div className="flex flex-wrap gap-1 max-w-[220px]">
                    {(r.products || []).map((p) => <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-[color:var(--aii-green-tint)] text-brand-dark">{p}</span>)}
                  </div>
                </td>
                <td className="text-xs max-w-[180px]">{r.destinations || <span className="text-[color:var(--aii-muted)]">—</span>}</td>
                <td className="text-right" data-testid={`partner-revenue-${r.id}`}>
                  <PartnerRevenueCell rev={revenue[r.id]} />
                </td>
                <td><StatusPill s={r.status} /></td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`edit-${r.id}`} onClick={() => { setEditing(r); setShowForm(true); }} title="Edit" className="p-1 text-[color:var(--aii-muted)] hover:text-brand"><Pencil size={13} /></button>
                    <button data-testid={`del-${r.id}`} onClick={() => del(r.id)} title="Delete" className="p-1 text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
